import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import { getDocumotoPartsManualSearch } from '../../api/documoto';
import type { DocumotoManualNodeDto } from '../../api/documoto/types';
import { DocumotoTocTree } from '../../components/Documoto/DocumotoTocTree';
import '../../components/Documoto/documoto-reference.scss';

type LocationState = {
	serial?: string;
	autoSearch?: boolean;
};

const DocumotoPartsSearchPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const autoHandled = useRef(false);

	const [serial, setSerial] = useState(() => searchParams.get('serial')?.trim() ?? '');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [roots, setRoots] = useState<DocumotoManualNodeDto[] | null>(null);

	const canSubmit = useMemo(() => serial.trim().length > 0 && !busy, [busy, serial]);

	const runSearch = useCallback(async (serialQuery: string) => {
		const s = serialQuery.trim();
		if (!s) {
			return;
		}
		setBusy(true);
		setError(null);
		setRoots(null);
		try {
			const res = await getDocumotoPartsManualSearch(s);
			if (res.errorMessage) {
				setError(res.errorMessage);
				return;
			}
			const list = res.chaptersAndPages ?? [];
			setRoots(list);
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setBusy(false);
		}
	}, []);

	useEffect(() => {
		const st = (location.state ?? null) as LocationState | null;
		if (st?.serial?.trim()) {
			setSerial(st.serial.trim());
		}
		if (!st?.autoSearch || !st?.serial?.trim()) {
			return;
		}
		if (autoHandled.current) {
			return;
		}
		autoHandled.current = true;
		navigate('.', { replace: true, state: {} });
		void runSearch(st.serial.trim());
	}, [location.state, navigate, runSearch]);

	async function onSubmit(e: FormEvent) {
		e.preventDefault();
		if (!canSubmit) {
			return;
		}
		await runSearch(serial);
	}

	return (
		<Container fluid className="py-3">
			<Row>
				<Col lg={11} xl={10}>
					<div className="documoto-ref">
						<h1 className="h4 mb-3">Parts manual search</h1>

						<Card className="mb-4 border-0 shadow-sm">
							<CardBody>
								<Form onSubmit={onSubmit} className="serial-search">
									<FormGroup>
										<Label for="documoto-serial" className="serial-search-label">
											Serial number
										</Label>
										<Input
											id="documoto-serial"
											className="serial-search-input"
											type="text"
											autoComplete="off"
											value={serial}
											onChange={(ev) => setSerial(ev.target.value)}
											placeholder="Enter serial number"
										/>
										<p className="serial-search-hint">Search for the parts manual for this machine.</p>
									</FormGroup>
									<Button color="primary" type="submit" className="documoto-serial-submit" disabled={!canSubmit}>
										{busy ? 'Searching…' : 'Find parts manual'}
									</Button>
								</Form>
							</CardBody>
						</Card>

						{error ? (
							<Alert color="danger" role="alert">
								{error}
							</Alert>
						) : null}

						{roots !== null && roots.length > 0 ? (
							<section className="toc-results">
								<h2 className="h6 mb-3 text-muted text-uppercase small">Parts manual contents</h2>
								<DocumotoTocTree rootNodes={roots} returnSerial={serial.trim()} />
							</section>
						) : null}
					</div>
				</Col>
			</Row>
		</Container>
	);
};

export default DocumotoPartsSearchPage;
